import React, { Component, createRef } from 'react';
import {
  bool, func, number, string, oneOf
} from 'prop-types';
import classNames from 'classnames';
import { ExperienceContext, getDomPath } from '@thd-nucleus/experience-context';
import './Pagination.style.scss';
import { publish } from '../publish';

class Pagination extends Component {
  // eslint-disable-next-line react/static-property-placement
  static contextType = ExperienceContext;

  gotoPage = (page) => {
    const { onPageChanged, totalPages } = this.props;
    const newPage = Math.max(0, Math.min(page, totalPages));
    onPageChanged(newPage);
  };

  handleClick = (page, evt) => {
    evt.preventDefault();
    this.gotoPage(page.index);
  };

  range = (start, end = 1) => {
    const newEnd = (typeof end !== 'number' ? start : end) + 1;
    return [...Array(newEnd).keys()].slice(start, newEnd);
  };

  fetchPageArrows = (pageRange) => {
    const { totalPages, currentPage } = this.props;
    let pages;
    const leftArrow = {
      index: currentPage - 1,
      type: 'leftArrow'
    };
    const rightArrow = {
      index: currentPage + 1,
      type: 'rightArrow'
    };

    if (currentPage !== 1 && currentPage === totalPages) {
      pages = [leftArrow, ...pageRange, { index: 1, type: 'rightArrow' }];
    } else if (currentPage === 1 && currentPage !== totalPages) {
      pages = [...pageRange, rightArrow];
    } else if (currentPage !== 1 && currentPage !== totalPages) {
      pages = [leftArrow, ...pageRange, rightArrow];
    }

    return pages;
  };

  fetchMultiplePages = () => {
    const {
      currentPage,
      leftNeighbors,
      maxNumericBlocks,
      pagesBeforeDotsShow,
      rightNeighbors,
      totalPages
    } = this.props;

    const leftDots = {
      index: currentPage - leftNeighbors,
      type: 'left'
    };
    const rightDots = {
      index: 1,
      type: 'right'
    };

    let pages = [];

    if (currentPage <= pagesBeforeDotsShow) {
      pages = this.range(1, maxNumericBlocks);
    } else if (currentPage <= totalPages - rightNeighbors) {
      const extraPages = this.range(
        currentPage - leftNeighbors,
        currentPage + rightNeighbors
      );
      pages = [1, leftDots, ...extraPages];
    } else if (currentPage === totalPages - 1) {
      const extraPages = this.range(
        currentPage - leftNeighbors,
        totalPages
      );
      pages = [1, 2, leftDots, ...extraPages];
    } else if (currentPage === totalPages) {
      pages = [totalPages - 1, totalPages, rightDots, 1, 2, 3];
    }

    return pages.map((page) => {
      if (typeof page !== 'number') {
        return page;
      }
      return {
        index: page,
        type: 'number'
      };
    });

  }

  fetchPageNumbers = () => {
    const { maxNumericBlocks, totalPages } = this.props;

    if (totalPages > maxNumericBlocks) {
      return this.fetchMultiplePages();
    }

    const range = this.range(1, totalPages).map((index) => ({ index, type: 'number' }));
    return range;
  };

  pageList = () => {
    const { showArrows } = this.props;
    let pages = this.fetchPageNumbers();

    if (showArrows && pages.length !== 0) {
      pages = this.fetchPageArrows(pages);
    }

    let {
      queryHandler,
      href = '',
      currentPage,
      className = '',
      noQueryParams
    } = this.props;

    let [path, querystring = ''] = href.split('?');
    let name = 'startindex';

    if (querystring) {
      querystring = '&' + querystring;
    }

    return pages.map((page, index) => {
      let value = page;
      if (queryHandler) {
        const result = queryHandler(name, page.index);
        name = result.name;// eslint-disable-line
        value = result.value;// eslint-disable-line
      }

      if (page.type === 'left' || page.type === 'right') {
        return (
          <li key={index} className="hd-pagination__item">
            <span className="hd-pagination__dots">...</span>
          </li>
        );
      }
      if ((page.type === 'leftArrow' || page.type === 'rightArrow') && href === '') {
        const arialabel = page.type === 'leftArrow' ? 'Previous' : 'Next';
        const ref = createRef();
        return (
          <li key={index} className="hd-pagination__item hd-pagination__button">
            <button
              ref={ref}
              className="hd-pagination__link"
              aria-label={arialabel}
              type="button"
              onClick={(evt) => {
                this.handleClick(page, evt);
                const _path = getDomPath(ref);
                publish('click', { ...page, path: _path });
              }}
            />
          </li>
        );
      }
      if (page.type === 'number' && href === '') {
        const ref = createRef();
        return (
          <li
            key={index}
            className="hd-pagination__item"
          >
            <button
              ref={ref}
              className={`hd-pagination__link${
                currentPage === page.index ? ' hd-pagination__current' : ''}`}
              onClick={(evt) => {
                this.handleClick(page, evt);
                const _path = getDomPath(ref);
                publish('click', { ...page, path: _path });
              }}
              type="button"
            >
              {page.index}
            </button>
          </li>
        );
      }
      if (page.type === 'leftArrow' || page.type === 'rightArrow') {
        const arialabel = page.type === 'leftArrow' ? 'Previous' : 'Next';
        const ref = createRef();
        return (
          <li key={index} className="hd-pagination__item hd-pagination__button">
            <a
              ref={ref}
              className={`hd-pagination__link ${className}`}
              href={noQueryParams ? `${value}` : `${path}?${name}=${value}${querystring}`}
              aria-label={arialabel}
              onClick={(evt) => {
                this.handleClick(page, evt);
                const _path = getDomPath(ref);
                publish('click', { ...page, path: _path });
              }}
            >
              {/* content goes here */}
            </a>
          </li>
        );
      }
      if (page.type === 'number') {
        const ref = createRef();
        return (
          <li
            key={index}
            className="hd-pagination__item"
          >
            <a
              ref={ref}
              className={`hd-pagination__link ${className}${
                currentPage === page.index ? ' hd-pagination__current' : ''
              }`}
              href={noQueryParams ? `${value}` : `${path}?${name}=${value}${querystring}`}
              onClick={(evt) => {
                this.handleClick(page, evt);
                const _path = getDomPath(ref);
                publish('click', { ...page, path: _path });
              }}
            >
              {page.index}
            </a>
          </li>
        );
      }
      return null;
    });
  };

  render() {
    const { channel } = this.context;
    const { classNameWrapper, totalPages } = this.props;
    if (totalPages <= 1) return null;
    const paginationClasses = classNames(
      'hd-pagination',
      classNameWrapper, {
        'hd-pagination--mobile': channel === 'mobile'
      }
    );
    return (
      <nav className={paginationClasses} role="navigation">
        <ul className="hd-pagination__wrapper">
          {this.pageList()}
        </ul>
      </nav>
    );
  }
}

Pagination.propTypes = {
  className: string,
  classNameWrapper: string,
  currentPage: number.isRequired,
  href: string,
  /** Number of numeric blocks left of current page */
  leftNeighbors: number,
  /** Maximum number of numeric blocks */
  maxNumericBlocks: number,
  noQueryParams: bool,
  onPageChanged: func.isRequired,
  /** Page before 'dots' begin to appear */
  pagesBeforeDotsShow: number,
  queryHandler: func,
  /** Number of numeric blocks right of current page */
  rightNeighbors: number,
  showArrows: bool,
  totalPages: number.isRequired,
};

Pagination.defaultProps = {
  className: '',
  classNameWrapper: null,
  href: '',
  leftNeighbors: 1,
  maxNumericBlocks: 5,
  noQueryParams: false,
  pagesBeforeDotsShow: 4,
  queryHandler: null,
  rightNeighbors: 2,
  showArrows: false
};

export { Pagination };
